import { useTranslation } from '@e-kyc/translations';
import axios from 'axios';
import { useEffect, useState } from 'react';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

import { Button } from '../../../../components';
import { useEkyc, type EkycStep } from '../../../../providers';

interface ConsentContainerProps {
  consentContentUrl: string;
  nextStep: EkycStep;
}

export default function ConsentContainer(props: Readonly<ConsentContainerProps>) {
  const { consentContentUrl, nextStep } = props;
  const { t, i18n } = useTranslation();

  const { language } = i18n;

  const { goBackToPreviousStep, setStep } = useEkyc();

  const [content, setContent] = useState<string>('');

  useEffect(() => {
    const fetchContent = async () => {
      const localizedUrl = consentContentUrl.replace('{locale}', language);
      try {
        const { data } = await axios.get(localizedUrl);
        setContent(data);
      } catch (error) {
        console.error('Error fetching consent content:', error);
        setContent('');
      }
    };
    fetchContent();
  }, [language, consentContentUrl]);

  return (
    <div className='mx-auto flex h-dvh max-w-md flex-col overflow-hidden'>
      <div className='flex h-dvh flex-col items-center justify-center pt-4 pb-20'>
        <div className='mb-2 flex h-full w-full flex-col gap-6 overflow-y-scroll px-4 pb-4'>
          <Markdown
            remarkPlugins={[remarkGfm]}
            components={{
              h2: ({ children, ...props }) => (
                <h2 {...props} className='text-center'>
                  {children}
                </h2>
              ),
              p: ({ ...props }) => <p className='text-body1' {...props} />,
              del: ({ children }) => <p className='font-semibold underline'>{children}</p>,
            }}
          >
            {content}
          </Markdown>
        </div>
      </div>
      <div className='fixed bottom-0 z-100 mx-auto h-fit w-full max-w-md border-t-1 border-gray-300 bg-white'>
        <div className='flex h-fit w-full gap-4 px-4 pt-4 pb-8'>
          <Button variant='outlined' fullWidth onClick={() => goBackToPreviousStep()}>
            {t('button.back')}
          </Button>
          <Button fullWidth onClick={() => setStep(nextStep)}>
            {t('button.accept')}
          </Button>
        </div>
      </div>
    </div>
  );
}
